import React, { useState } from 'react'
import styled from '@emotion/styled'

import { Container } from 'components/layout'
import { COLORS, PAGE_BREAKPOINTS } from 'parameters/index'
import { Partner } from 'services/ContentfulService/models/partner/partner'
import { Dropdown } from 'components/elements'
import { CustomSelectOption } from 'types'
import { Breadcrumbs } from 'components/elements/Breadcrumbs'
import { LearnOverride } from '../common/shared.styles'
import { PartnerCard, PartnerItemWrapper } from 'experiences/learn/partners/PartnerCard'
import { Product } from 'services/ContentfulService/models/product'

interface Props {
	partners: Partner[]
	initialTopic: string | undefined
	products: Product[]
}

export const PartnerDirectoryPage = ({ partners, products }: Props): JSX.Element => {
	const getAllCategories = (): string[] =>
		products.map(p => p.name).filter(p => partners.map(n => n.products[0]?.name).includes(p))

	const createOptions = (): CustomSelectOption[] => {
		const options = [{ value: 'All', label: 'All' }]
		getAllCategories().map(item => {
			options.push({ value: item, label: item })
		})
		return options
	}

	const [filter, setFilter] = useState(createOptions()[0])

	const getFilteredPartners = (filter: any): Partner[] => {
		return partners
			.filter(partner => partner.products?.some(product => product.name === filter))
			.sort((a, b) => a.displayRank - b.displayRank)
	}

	const getFilteredCategories = (filter: string): string[] => {
		if (!filter || filter === 'All') {
			return getAllCategories()
		}
		return getAllCategories().filter(item => item === filter)
	}

	return (
		<Container>
			<Breadcrumbs breadcrumbs={[{ name: 'Learn', url: '/learn' }]} />
			<Top>
				<div>
					<Title>Our network of providers</Title>
					<SubTitle> One search gives you access to a vast network of offers</SubTitle>
				</div>
				<LearnOverride>
					<Dropdown options={createOptions()} onChange={setFilter} />
				</LearnOverride>
			</Top>
			{getFilteredCategories(filter.value)
				.filter(x => x && x != '')
				.map((item: string) => {
					return (
						<Partners key={item}>
							<PartnerTitle>{item}</PartnerTitle>
							<PartnersWrapper>
								{getFilteredPartners(item).map(partner => (
									<PartnerCard key={partner.id} partner={partner} hideDescription />
								))}
							</PartnersWrapper>
						</Partners>
					)
				})}
		</Container>
	)
}

const Top = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	@media only screen and (${PAGE_BREAKPOINTS.DESKTOP_SMALL}) {
		flex-direction: column;
		align-items: flex-start;
		margin-bottom: 40px;
	}
`

const Title = styled.h1`
	margin: 16px 0 10px 0;
	font-size: 40px;
	line-height: 1.2;
`

const SubTitle = styled.h5`
	margin-bottom: 32px;
	font-size: 20px;
	font-weight: normal;
	line-height: 1.4;
	color: ${COLORS.SPANISH_GRAY};
	@media only screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
		margin-bottom: 16px;
	}
`

const Partners = styled.div`
	display: flex;
	flex-direction: column;
`

const PartnersWrapper = styled.div`
	display: grid;
	grid-gap: 24px;
	grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
	padding: 32px 0 96px 0;

	@media only screen and (${PAGE_BREAKPOINTS.TABLET_LANDSCAPE}) {
		grid-gap: 16px;
		grid-template-columns: 1fr 1fr 1fr 1fr;
		padding: 24px 0 48px 0;
	}

	@media only screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
		grid-gap: 16px;
		grid-template-columns: 1fr 1fr 1fr;
	}

	@media only screen and (${PAGE_BREAKPOINTS.MOBILE}) {
		grid-template-columns: 1fr 1fr;
	}

	${PartnerItemWrapper} {
		@media only screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
			width: unset;
			padding: 0;
		}

		img {
			margin-bottom: 0;
		}
	}
`

const PartnerTitle = styled.h3`
	font-size: 32px;
	font-weight: bold;
	line-height: 1.81;
`
